<template>
  <div
    class="sticky-bottom-bar-icons"
    :style="{ gridTemplateColumns: `repeat(${buttonsCount}, 1fr)` }"
  >
    <button
      v-if="wizardProps.activeTabIndex > 0"
      class="mobile-bar-btn mobile-bar-btn-simple"
      type="button"
      @click="wizardProps.prevTab()"
    >
      <div class="mobile-bar-btn-content">
        <i class="feather icon-arrow-left mobile-bar-icon"></i>
        <span class="mobile-btn-label">{{ $t('$General.Back') }}</span>
      </div>
    </button>

    <button
      class="mobile-bar-btn mobile-bar-btn-simple"
      type="button"
      @click="$emit('reset')"
    >
      <div class="mobile-bar-btn-content">
        <i class="feather mobile-bar-icon" :class="resetButtonIcon"></i>
        <span class="mobile-btn-label">{{ resetButtonText }}</span>
      </div>
    </button>

    <button
      v-if="showSaveAndQuitButton"
      class="mobile-bar-btn mobile-bar-btn-simple"
      type="button"
      @click="onSaveAndQuit"
    >
      <div class="mobile-bar-btn-content">
        <i class="feather icon-save mobile-bar-icon"></i>
        <span class="mobile-btn-label">{{ $t('$General.SaveAndQuit') }}</span>
      </div>
    </button>

    <button
      v-if="!wizardProps.isLastStep"
      class="mobile-bar-btn mobile-bar-btn-simple"
      type="button"
      @click="wizardProps.nextTab()"
    >
      <div class="mobile-bar-btn-content">
        <i class="feather icon-arrow-right mobile-bar-icon"></i>
        <span class="mobile-btn-label">{{ $t('$General.Continue') }}</span>
      </div>
    </button>

    <template v-if="wizardProps.isLastStep">
      <button
        class="mobile-bar-btn mobile-bar-btn-simple"
        type="button"
        @click="$emit('test')"
      >
        <div class="mobile-bar-btn-content">
          <i class="feather icon-tablet mobile-bar-icon"></i>
          <span class="mobile-btn-label">{{ $t('$General.Test') }}</span>
        </div>
      </button>

      <button
        class="mobile-bar-btn mobile-bar-btn-simple"
        type="button"
        :class="{ disabled: !validSend }"
        @click="!validSend ? null : wizardProps.nextTab()"
      >
        <div class="mobile-bar-btn-content">
          <i class="feather mobile-bar-icon" :class="saveButtonIcon"></i>
          <span class="mobile-btn-label">{{ saveButtonText }}</span>
        </div>
      </button>
    </template>
  </div>
</template>

<script>
import enums from '@/enums';

/**
 * Component to shot footer buttons in wizard campaign
 *
 * @module views/modules/campaigns/CampaignWizardFooterButtons
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 * @vue-prop {Object} wizardProps - props of campaign wizard
 * @vue-prop {Object} validSend - indicate if button to send must be valid or no
 * @vue-prop {boolean} isDraft - indicate if the campaign is a draft
 * @vue-prop {string} deliveryType - the delivery type of the campaign
 * @vue-prop {string} operation - indicate the campaign operation
 * @vue-computed {string} resetButtonText - text to show in the reset button
 * @vue-computed {string} resetButtonIcon - icon of reset button
 * @vue-computed {boolean} showSaveAndQuitButton - indicate if show or no the save and quit button
 * @vue-event {void} onSaveAndQuit - called on continue and save
 */
export default {
  name: 'CampaignWizardFooterButtons',
  components: {
  },
  props: {
    wizardProps: {
      type: Object,
      required: true,
    },
    validSend: {
      type: Boolean,
      required: true,
    },
    validSave: {
      type: Boolean,
      required: false,
      default: true,
    },
    isDraft: {
      type: Boolean,
      required: false,
      default: false,
    },
    deliveryType: {
      type: String,
      required: true,
      validator(value) {
        return Object.values(enums.Campaign.DeliveryType).includes(value);
      },
    },
    operation: {
      type: String,
      required: true,
      validator(value) {
        return [
          enums.Operation.CREATE,
          enums.Operation.EDIT,
          enums.Operation.CLONE,
        ].indexOf(value) !== -1;
      },
    },
  },
  computed: {
    buttonsCount() {
      // Reset is always visible; the rest are conditional based on wizard state
      const count = 1
        + (this.wizardProps.activeTabIndex > 0 ? 1 : 0)
        + (this.showSaveAndQuitButton ? 1 : 0)
        + (this.wizardProps.isLastStep ? 2 : 1); // last: Test + Send/Schedule, else: Continue
      return count;
    },
    resetButtonText() {
      if (this.isDraft) {
        return this.$t('$CampaignsModules.DiscardDraft');
      }

      if (this.operation === this.$enums.Operation.EDIT
        && this.deliveryType === this.$enums.Campaign.DeliveryType.LATER) {
        return this.$t('$General.Cancel');
      }

      return this.$t('$General.StartOver');
    },
    resetButtonIcon() {
      if (this.isDraft) {
        return 'icon-trash-2';
      }

      if (this.operation === this.$enums.Operation.EDIT
        && this.deliveryType === this.$enums.Campaign.DeliveryType.LATER) {
        return 'icon-x';
      }

      return 'icon-rotate-ccw';
    },
    showSaveAndQuitButton() {
      return this.validSave
        && (this.operation === this.$enums.Operation.CREATE
        || this.isDraft);
    },
    saveButtonText() {
      return this.deliveryType === this.$enums.Campaign.DeliveryType.IMMEDIATELY
        ? this.$t('$General.Send')
        : this.$t('$General.Schedule');
    },
    saveButtonIcon() {
      return this.deliveryType === this.$enums.Campaign.DeliveryType.IMMEDIATELY
        ? 'icon-send'
        : 'icon-clock';
    },
  },
  methods: {
    onSaveAndQuit() {
      this.$emit('save-and-quit');
    },
  },
};
</script>
